import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Heading, Text } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import { ChevronDown } from 'lucide-react';

// Helper function to map activeMain sections to URL paths
const getSectionPath = (section, selectedCompanyId, selectedJobId) => {
    switch (section) {
        case 'dashboard': return `/candidate-admin-home`;
        case 'all-candidate': return `/all-candidate`;
        case 'add-candidate': return `/add-candidate`;
        case 'candidate-profile': return '/candidate-profile';
        case 'edit-company-profile': return selectedCompanyId ? `/edit-company-profile?companyID=${encodeURIComponent(selectedCompanyId)}` : `/all-companies`;
        case 'view-jobs': return selectedCompanyId ? `/all-jobs?companyID=${encodeURIComponent(selectedCompanyId)}` : `/all-companies`;
        case 'create-jd': return selectedCompanyId ? `/create-JD?companyID=${encodeURIComponent(selectedCompanyId)}` : `/all-companies`;
        case 'upload-jd': return selectedCompanyId ? `/upload-JD?companyID=${encodeURIComponent(selectedCompanyId)}` : `/all-companies`;
        // case 'generate-jd': return selectedCompanyId ? `${baseUrl}/companies/${selectedCompanyId}/jobs/generate-ai` : `${baseUrl}/companies`;
        case 'job-description': return (selectedCompanyId && selectedJobId) ? `/job-description?companyID=${encodeURIComponent(selectedCompanyId)}&jobID=${encodeURIComponent(selectedJobId)}` : (selectedCompanyId ? `/all-jobs?companyID=${encodeURIComponent(selectedCompanyId)}` : `/all-companies`);
        // case 'view-scheduled-tests': return (selectedCompanyId && selectedJobId) ? `/companies/${selectedCompanyId}/jobs/${selectedJobId}/scheduled-tests` : (selectedCompanyId ? `/companies/${selectedCompanyId}/jobs` : `/companies`);
        case 'view-all-candidates': return (selectedCompanyId && selectedJobId) ? `/all-candidates?companyID=${encodeURIComponent(selectedCompanyId)}&jobID=${encodeURIComponent(selectedJobId)}` : (selectedCompanyId ? `/all-jobs?companyID=${encodeURIComponent(selectedCompanyId)}` : `/all-companies`);
        case 'edit-job': return (selectedCompanyId && selectedJobId) ? `/edit-job?companyID=${encodeURIComponent(selectedCompanyId)}&jobID=${encodeURIComponent(selectedJobId)}` : (selectedCompanyId ? `/all-jobs?companyID=${encodeURIComponent(selectedCompanyId)}` : `/all-companies`);
        case 'view-scheduled-tests':
  return (selectedCompanyId && selectedJobId)
    ? `/view-test?companyID=${encodeURIComponent(selectedCompanyId)}&jobID=${encodeURIComponent(selectedJobId)}`
    : '/company-admin-home';

        default: return `/candidate-admin-home`; // Fallback
    }
};

export default function SideBar({ activeMain, setActiveMain, selectedCompany, selectedJob, setSelectedCompany, setSelectedJob, onDeleteCompanyClick, onDeleteJobClick, onEnrollCandidateClick }) {
    const navigate = useNavigate(); // Initialize useNavigate
    const location = useLocation(); // Get current URL location

    const jobID = new URLSearchParams(location.search).get("jobID");
    const companyID = new URLSearchParams(location.search).get("companyID");
    const [showCompanyActions, setShowCompanyActions] = useState(false);
    const [showJobActions, setShowJobActions] = useState(false);
    const [showPostJobDropdown, setShowPostJobDropdown] = useState(false);

    const postJobDropdownRef = useRef(null);

    useEffect(() => {
        const isCompanyActionActive = companyID || ['company-profile', 'edit-company-profile', 'delete-company-profile',
            'view-jobs', 'post-job', 'enroll-candidate', 'view-scheduled-tests', 'view-all-candidates',
            'edit-job', 'delete-job', 'job-description',
            'create-jd', 'upload-jd', 'generate-jd'
        ].includes(activeMain);
        setShowCompanyActions(isCompanyActionActive);

        const isJobActionActive = jobID || ['enroll-candidate', 'view-scheduled-tests', 'view-all-candidates',
            'edit-job', 'delete-job', 'job-description'
        ].includes(activeMain);
        setShowJobActions(isJobActionActive);

    }, [activeMain, companyID, jobID]);

    useEffect(() => {
        console.log("sidebar", location.state)
        const handleClickOutside = (event) => {
            if (postJobDropdownRef.current && !postJobDropdownRef.current.contains(event.target)) {
                setShowPostJobDropdown(false);
            }
        };

        if (showPostJobDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showPostJobDropdown]);


    const handleMainClick = (section) => {
        // Special handling for popups (no navigation, just show popup)
        if (section === 'delete-company-profile') {
            if (companyID) {
                onDeleteCompanyClick();
            } else {
                alert('Please select a company to delete first.');
                navigate(getSectionPath('all-companies')); // Navigate to all companies if no company selected
            }
            return; // Prevent further navigation
        } else if (section === 'delete-job') {
            if (jobID) {
                onDeleteJobClick();
            } else {
                alert('Please select a job to delete first.');
                navigate(getSectionPath('view-jobs', companyID)); // Navigate to view jobs if no job selected
            }
            return;
        } else if (section === 'enroll-candidate') {
            if (jobID) {
                onEnrollCandidateClick();
            } else {
                alert('Please select a job to enroll a candidate for first.');
                navigate(getSectionPath('view-jobs', companyID)); // Navigate to view jobs if no job selected
            }
            return;
        } else if (section === 'post-job-toggle') { // Toggles the dropdown, no navigation
            setShowPostJobDropdown(prev => !prev);
            return;
        }

        // For all other navigation actions:
        const path = getSectionPath(section, companyID, jobID);
        navigate(path); // Change the URL
        setShowPostJobDropdown(false); // Close dropdown on navigation

        // Reset selectedCompany/Job based on navigation context
        if (section === 'all-companies' || section === 'dashboard' || section === 'add-company') {
            setSelectedCompany(null);
            setSelectedJob(null);
            console.log("Selected company and job cleared");
        } else if (['company-profile', 'view-jobs', 'post-job', 'create-jd', 'upload-jd', 'generate-jd', 'edit-company-profile'].includes(section)) {
            setSelectedJob(null); // Clear job if navigating to a company-level action
            console.log("job cleared");
        }
    };

    return (
        <div className={cn(
            "w-[250px] min-w-[250px] bg-[#050F47] text-white p-3 shadow-md",
            "flex flex-col gap-2.5 font-['Poppins',sans-serif]"
        )}>
            <Button
                variant="ghost"
                className={cn(
                    "text-base font-['Poppins',sans-serif] text-white p-3.5 px-4 text-left rounded-lg",
                    "bg-[#050F47] hover:bg-[#03157d] transition-all",
                    activeMain === 'dashboard' && "bg-[#142DB7]"
                )}
                onClick={() => handleMainClick('dashboard')}
            >
                Dashboard
            </Button>

            <Button
                variant="ghost"
                className={cn(
                    "text-base font-['Poppins',sans-serif] text-white p-3.5 px-4 text-left rounded-lg",
                    "bg-[#050F47] hover:bg-[#03157d] transition-all",
                    (activeMain === 'all-candidate' || showCompanyActions) && "bg-[#142DB7]"
                )}
                onClick={() => handleMainClick('all-candidate')}
            >
                All Candidate
            </Button>

            <Button
                variant="ghost"
                className={cn(
                    "text-base font-['Poppins',sans-serif] text-white p-3.5 px-4 text-left rounded-lg",
                    "bg-[#050F47] hover:bg-[#03157d] transition-all",
                    activeMain === 'add-candidate' && "bg-[#142DB7]"
                )}
                onClick={() => handleMainClick('add-candidate')}
            >
                Add Candidate Profile
            </Button>

            {showCompanyActions && (
                <>
                    <Separator className="w-full my-0" />
                    <div className="relative" ref={postJobDropdownRef}>
                        <Button
                            variant="ghost"
                            className={cn(
                                "text-base font-['Poppins',sans-serif] text-white p-3.5 px-4 text-left rounded-lg w-full justify-between",
                                "bg-[#050F47] hover:bg-[#03157d] transition-all",
                                ['post-job', 'create-jd', 'upload-jd', 'generate-jd', 'after-jd'].includes(activeMain) && "bg-[#142DB7]"
                            )}
                            onClick={() => handleMainClick('post-job-toggle')}
                        >
                            Post Job
                            <ChevronDown className={cn("w-4 h-4 transition-transform", showPostJobDropdown && "rotate-180")} />
                        </Button>
                        <div className={cn(
                            "flex flex-col gap-2.5 mt-2.5 overflow-hidden transition-all",
                            showPostJobDropdown ? "max-h-96" : "max-h-0"
                        )}>
                            <Button
                                variant="ghost"
                                className={cn(
                                    "text-base font-['Poppins',sans-serif] text-white p-3.5 pl-7.5 text-left rounded-lg",
                                    "bg-[#050F47] hover:bg-[#03157d] transition-all",
                                    activeMain === 'create-jd' && "bg-[#142DB7]"
                                )}
                                onClick={() => handleMainClick('create-jd')}
                            >
                                Create JD
                            </Button>
                            <Button
                                variant="ghost"
                                className={cn(
                                    "text-base font-['Poppins',sans-serif] text-white p-3.5 pl-7.5 text-left rounded-lg",
                                    "bg-[#050F47] hover:bg-[#03157d] transition-all",
                                    activeMain === 'upload-jd' && "bg-[#142DB7]"
                                )}
                                onClick={() => handleMainClick('upload-jd')}
                            >
                                Upload JD
                            </Button>
                            <Button
                                variant="ghost"
                                className={cn(
                                    "text-base font-['Poppins',sans-serif] text-white p-3.5 pl-7.5 text-left rounded-lg",
                                    "bg-[#050F47] hover:bg-[#03157d] transition-all",
                                    activeMain === 'generate-jd' && "bg-[#142DB7]"
                                )}
                                onClick={() => handleMainClick('generate-jd')}
                            >
                                Generate JD with AI
                            </Button>
                        </div>
                    </div>
                </>
            )}

            {showJobActions && (
                <>
                    <Separator className="w-full my-0" />
                    <Heading level="h3" className="text-lg px-4 py-2 font-semibold m-0">Job Actions</Heading>
                    <Button
                        variant="ghost"
                        className={cn(
                            "text-base font-['Poppins',sans-serif] text-white p-3.5 px-4 text-left rounded-lg",
                            "bg-[#050F47] hover:bg-[#03157d] transition-all",
                            activeMain === 'job-description' && "bg-[#142DB7]"
                        )}
                        onClick={() => handleMainClick('job-description')}
                    >
                        Job Description
                    </Button>
                    <Button
                        variant="ghost"
                        className={cn(
                            "text-base font-['Poppins',sans-serif] text-white p-3.5 px-4 text-left rounded-lg",
                            "bg-[#050F47] hover:bg-[#03157d] transition-all",
                            activeMain === 'enroll-candidate' && "bg-[#142DB7]"
                        )}
                        onClick={() => handleMainClick('enroll-candidate')}
                    >
                        Enroll Candidate
                    </Button>
                    <Button
                        variant="ghost"
                        className={cn(
                            "text-base font-['Poppins',sans-serif] text-white p-3.5 px-4 text-left rounded-lg",
                            "bg-[#050F47] hover:bg-[#03157d] transition-all",
                            activeMain === 'view-scheduled-tests' && "bg-[#142DB7]"
                        )}
                        onClick={() => handleMainClick('view-scheduled-tests')}
                    >
                        View Scheduled Tests
                    </Button>
                    <Button
                        variant="ghost"
                        className={cn(
                            "text-base font-['Poppins',sans-serif] text-white p-3.5 px-4 text-left rounded-lg",
                            "bg-[#050F47] hover:bg-[#03157d] transition-all",
                            activeMain === 'view-all-candidates' && "bg-[#142DB7]"
                        )}
                        onClick={() => handleMainClick('view-all-candidates')}
                    >
                        View All Candidates
                    </Button>
                    <Button
                        variant="ghost"
                        className={cn(
                            "text-base font-['Poppins',sans-serif] text-white p-3.5 px-4 text-left rounded-lg",
                            "bg-[#050F47] hover:bg-[#03157d] transition-all",
                            activeMain === 'edit-job' && "bg-[#142DB7]"
                        )}
                        onClick={() => handleMainClick('edit-job')}
                    >
                        Edit Job
                    </Button>
                    <Button
                        variant="ghost"
                        className={cn(
                            "text-base font-['Poppins',sans-serif] text-white p-3.5 px-4 text-left rounded-lg",
                            "bg-[#050F47] hover:bg-[#03157d] transition-all",
                            activeMain === 'delete-job' && "bg-[#142DB7]"
                        )}
                        onClick={() => handleMainClick('delete-job')}
                    >
                        Delete Job
                    </Button>
                </>
            )}
        </div>
    );
}
